import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { translateMessage } from '@/locales';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomeGreenView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Главная', breadcrumbKey: 'pages.home' }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Вход', breadcrumbKey: 'pages.login' }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Регистрация', breadcrumbKey: 'pages.register' }
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Верификация', breadcrumbKey: 'pages.verifyEmail' }
    },
    // Публичная платежная форма (без авторизации)
    {
      path: '/checkout/:payment_id',
      name: 'payment-checkout',
      component: () => import('@/views/PaymentCheckoutView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Оплата', breadcrumbKey: 'pages.checkout' }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Сброс пароля', breadcrumbKey: 'pages.resetPassword' }
    },
    {
      path: '/source-of-funds',
      name: 'source-of-funds',
      component: () => import('@/views/SourceOfFundsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Источник средств', breadcrumbKey: 'pages.sourceOfFunds' }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Дашборд', breadcrumbKey: 'sidebar.dashboard', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>' }
    },
    {
      path: '/accounts',
      name: 'accounts',
      component: () => import('@/views/AccountsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Кошельки', breadcrumbKey: 'sidebar.accounts', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>' }
    },
    {
      path: '/account-types-guide',
      name: 'account-types-guide',
      component: () => import('@/views/AccountTypesGuideView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Типы счетов', breadcrumbKey: 'sidebar.accountTypes', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4"></path><path d="M21 12c-1 0-2-1-2-2s1-2 2-2 2 1 2 2-1 2-2 2z"></path><path d="M3 12c1 0 2-1 2-2s-1-2-2-2-2 1-2 2 1 2 2 2z"></path><path d="M12 3c0 1-1 2-2 2s-2-1-2-2 1-2 2-2 2 1 2 2z"></path><path d="M12 21c0-1 1-2 2-2s2 1 2 2-1 2-2 2-2-1-2-2z"></path></svg>' }
    },
    {
      path: '/accounts/:id/settings',
      name: 'account-settings',
      component: () => import('@/views/AccountSettingsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Настройки счета', breadcrumbKey: 'pages.accountSettings' }
    },
    {
      path: '/deposit',
      name: 'deposit',
      component: () => import('@/views/DepositView.vue'),
      meta: { requiresAuth: true, requiresIdentity: true, breadcrumb: 'Пополнения', breadcrumbKey: 'sidebar.deposits', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20"/><path d="m5 12 7 7 7-7"/></svg>' }
    },
    {
      path: '/transactions',
      name: 'transactions',
      component: () => import('@/views/TransactionsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'История', breadcrumbKey: 'sidebar.transactions', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' }
    },
    {
      path: '/payments',
      name: 'payments',
      component: () => import('@/views/AlfabitPaymentsView.vue'),
      meta: { requiresAuth: true, requiresIdentity: true, breadcrumb: 'Оплата услуг', breadcrumbKey: 'pages.paymentServices', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>' }
    },
    {
      path: '/alfabit-payments',
      redirect: '/payments',
    },
    {
      path: '/odengi-payments',
      redirect: '/alfabit-payments',
    },
    {
      path: '/transfer',
      name: 'transfer',
      component: () => import('@/views/TransferView.vue'),
      meta: { requiresAuth: true, requiresIdentity: true, breadcrumb: 'Переводы', breadcrumbKey: 'sidebar.transfers', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="17" y1="7" x2="7" y2="17"></line><polyline points="17 17 7 17 7 7"></polyline></svg>' }
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('@/views/ReportsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Отчеты', breadcrumbKey: 'sidebar.reports' }
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Настройки', breadcrumbKey: 'sidebar.settings', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>' }
    },
    {
      path: '/api-keys',
      name: 'api-keys',
      component: () => import('@/views/ApiKeysView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'API Ключи', breadcrumbKey: 'sidebar.apiKeys', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>' }
    },
    {
      path: '/webhooks',
      name: 'webhooks',
      component: () => import('@/views/developer/WebhooksView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Webhooks', breadcrumbKey: 'sidebar.webhooks', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg>' }
    },
    {
      path: '/sandbox',
      name: 'sandbox',
      component: () => import('@/views/developer/SandboxView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Песочница', breadcrumbKey: 'sidebar.sandbox', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><path d="M9 9h6v6H9z"></path></svg>' }
    },
    {
      path: '/api-logs',
      name: 'api-logs',
      component: () => import('@/views/developer/ApiLogsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'API Логи', breadcrumbKey: 'sidebar.apiLogs', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>' }
    },
    {
      path: '/webhook-logs',
      name: 'webhook-logs',
      component: () => import('@/views/developer/WebhookLogsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Webhook Логи', breadcrumbKey: 'pages.webhookLogs', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg>' }
    },
    {
      path: '/team',
      name: 'team',
      component: () => import('@/views/TeamView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Моя команда', breadcrumbKey: 'sidebar.myTeam', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>' }
    },
    {
      path: '/verification',
      name: 'verification',
      component: () => import('@/views/VerificationView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Верификация аккаунта', breadcrumbKey: 'sidebar.verification', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4"></path><path d="M21 12c-1 0-2-1-2-2s1-2 2-2 2 1 2 2-1 2-2 2"></path><path d="M3 12c1 0 2-1 2-2s-1-2-2-2-2 1-2 2 1 2 2 2"></path></svg>' }
    },
    {
      path: '/phone-confirm',
      name: 'phone-confirm',
      component: () => import('@/views/PhoneConfirmView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Подтверждение телефона', breadcrumbKey: 'phoneReset.pageTitle' }
    },
    {
      path: '/limits-tariffs',
      name: 'limits-tariffs',
      component: () => import('@/views/LimitsTariffsView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Лимиты и Тарифы', breadcrumbKey: 'sidebar.limitsTariffs', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>' }
    },
    {
      path: '/fraud-report',
      name: 'fraud-report',
      component: () => import('@/views/FraudReportView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Сообщить о мошенничестве', breadcrumbKey: 'pages.fraudReport' }
    },
    {
      path: '/payment-form-builder',
      name: 'payment-form-builder',
      component: () => import('@/views/PaymentFormBuilderView.vue'),
      meta: { requiresAuth: true, breadcrumb: 'Конструктор форм', breadcrumbKey: 'sidebar.formBuilder', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>' }
    },
    {
      path: '/help',
      name: 'help',
      component: () => import('@/views/HelpView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Справка', breadcrumbKey: 'sidebar.help', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>' }
    },
    {
      path: '/api-documentation',
      name: 'api-documentation',
      component: () => import('@/views/ApiDocumentationView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'API Документация', breadcrumbKey: 'sidebar.apiDocumentation', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>' }
    },
    {
      path: '/docs/inbound',
      name: 'inbound-docs',
      component: () => import('@/views/docs/InboundIntegrationDocsView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Входящие пополнения', breadcrumbKey: 'sidebar.inboundDocs' }
    },
    {
      path: '/legal',
      name: 'legal',
      component: () => import('@/views/LegalView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Правовая информация', breadcrumbKey: 'sidebar.legal', icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>' }
    },
    {
      path: '/legal/user-agreement',
      name: 'user-agreement',
      component: () => import('@/views/legal/UserAgreementView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Пользовательское соглашение', breadcrumbKey: 'pages.userAgreement' }
    },
    {
      path: '/legal/privacy-policy',
      name: 'privacy-policy',
      component: () => import('@/views/legal/PrivacyPolicyView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Политика конфиденциальности', breadcrumbKey: 'pages.privacyPolicy' }
    },
    {
      path: '/legal/terms-of-service',
      name: 'terms-of-service',
      component: () => import('@/views/legal/TermsOfServiceView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Условия использования', breadcrumbKey: 'pages.termsOfService' }
    },
    {
      path: '/legal/nbkr-license',
      name: 'nbkr-license',
      component: () => import('@/views/legal/NBKRLicenseView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Лицензия НБКР', breadcrumbKey: 'pages.nbkrLicense' }
    },
    {
      path: '/legal/tariffs',
      name: 'legal-tariffs',
      component: () => import('@/views/legal/TariffsView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Лимиты электронного кошелька', breadcrumbKey: 'pages.tariffs' }
    },
    {
      path: '/legal/personal-data-consent',
      name: 'personal-data-consent',
      component: () => import('@/views/legal/PersonalDataConsentView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Согласие на сбор и обработку персональных данных', breadcrumbKey: 'pages.personalDataConsent' }
    },
    {
      path: '/legal/aml-policy',
      name: 'aml-policy',
      component: () => import('@/views/legal/AMLPolicyView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'AML/CFT политика', breadcrumbKey: 'pages.amlPolicy' }
    },
    {
      path: '/legal/payment-system-rules',
      name: 'payment-system-rules',
      component: () => import('@/views/legal/PaymentSystemRulesView.vue'),
      meta: { requiresAuth: false, breadcrumb: 'Правила функционирования ПС', breadcrumbKey: 'pages.paymentSystemRules' }
    },
    // 404 - редирект на главную (или на логин для неавторизованных)
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: () => {
        const userStore = useUserStore();
        return userStore.isAuthenticated ? { name: 'dashboard' } : { name: 'login' };
      },
      meta: { requiresAuth: false }
    }
  ]
});

export function applyRouteTitle(to: { meta: { breadcrumb?: unknown; breadcrumbKey?: unknown } }) {
  const key = typeof to.meta.breadcrumbKey === 'string' ? to.meta.breadcrumbKey : ''
  const translated = key ? translateMessage(key) : ''
  const breadcrumb = translated && translated !== key
    ? translated
    : (typeof to.meta.breadcrumb === 'string' ? to.meta.breadcrumb : '')
  document.title = breadcrumb
    ? `${breadcrumb} — AlfaBit Payments`
    : 'AlfaBit Payments'
}

router.afterEach((to) => {
  applyRouteTitle(to)
})

// Защита маршрутов
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();

  // Инициализация выполняется в App.vue, здесь только проверяем состояние
  // Удалили дублирование userStore.init() для устранения множественной инициализации
  
  const isAuthenticated = userStore.isAuthenticated;

  // Если маршрут требует авторизации, а пользователь не залогинен
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ name: 'login' });
  }
  
  // В клиентском фронтенде нет админских маршрутов
  
  // Если пользователь залогинен, не пускаем его на страницы входа/регистрации
  if (isAuthenticated && (to.name === 'login' || to.name === 'register')) {
    return next({ name: 'dashboard' });
  }

  // Если пользователь не залогинен и пытается попасть на защищенные страницы, перенаправляем на главную
  if (!isAuthenticated && to.meta.requiresAuth === true) {
    return next({ name: 'home' });
  }

  // Пополнение, переводы и оплата сервисов доступны только после верификации личности
  if (to.meta.requiresIdentity) {
    if (isAuthenticated && !userStore.user) {
      await userStore.init();
    }
    if (!userStore.isIdentityVerified) {
      return next({ name: 'verification' });
    }
  }

  next();
});

export default router; 